"use client";

import posthog from "posthog-js";
import { trackMixpanelEvent } from "@lucien-ai/ts";

export const landingAnalyticsEvents = {
  pageViewed: "landing_page_viewed",
  ctaClicked: "landing_cta_clicked",
  sectionViewed: "landing_section_viewed",
  scrollDepthReached: "landing_scroll_depth_reached",
  pricingPlanViewed: "landing_pricing_plan_viewed",
  proofSourceClicked: "landing_proof_source_clicked",
  faqOpened: "landing_faq_opened",
  formStarted: "landing_form_started",
  formSubmitted: "landing_form_submitted",
  formSucceeded: "landing_form_succeeded",
  formFailed: "landing_form_failed",
  thankYouViewed: "landing_thank_you_viewed",
} as const;

export type LandingAnalyticsEventName =
  (typeof landingAnalyticsEvents)[keyof typeof landingAnalyticsEvents];

export type LandingAnalyticsProperties = Record<string, unknown>;

export const LANDING_ATTRIBUTION_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "li_fat_id",
  "plan",
] as const;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const CLICK_ID_KEYS = ["gclid", "fbclid", "li_fat_id"] as const;
const FIRST_TOUCH_STORAGE_KEY = "lucien_landing_first_touch";
const LAST_TOUCH_STORAGE_KEY = "lucien_landing_last_touch";
const SESSION_STORAGE_KEY = "lucien_landing_session_id";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

type TouchSnapshot = {
  landing_page: string;
  initial_referrer: string | null;
  captured_at: string;
  params: Record<string, string>;
};

type SessionSnapshot = {
  id: string;
  last_seen_at: number;
};

function hasWindow() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function readJson<T>(key: string): T | null {
  if (!hasWindow()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be blocked; analytics should never break the page.
  }
}

function getCurrentPage() {
  if (!hasWindow()) return "";
  return `${window.location.pathname}${window.location.search}`;
}

function pickQueryParams(searchParams: URLSearchParams) {
  const params: Record<string, string> = {};

  for (const key of LANDING_ATTRIBUTION_QUERY_KEYS) {
    const value = searchParams.get(key);
    if (value) {
      params[key] = value;
    }
  }

  return params;
}

function hasAttributionParams(params: Record<string, string>) {
  return LANDING_ATTRIBUTION_QUERY_KEYS.some(key => Boolean(params[key]));
}

function createTouchSnapshot(): TouchSnapshot {
  const searchParams = new URLSearchParams(hasWindow() ? window.location.search : "");

  return {
    landing_page: getCurrentPage(),
    initial_referrer: hasWindow() && document.referrer ? document.referrer : null,
    captured_at: new Date().toISOString(),
    params: pickQueryParams(searchParams),
  };
}

function getFirstTouch() {
  const existing = readJson<TouchSnapshot>(FIRST_TOUCH_STORAGE_KEY);
  if (existing) return existing;

  const snapshot = createTouchSnapshot();
  writeJson(FIRST_TOUCH_STORAGE_KEY, snapshot);
  return snapshot;
}

function getLastTouch() {
  const existing = readJson<TouchSnapshot>(LAST_TOUCH_STORAGE_KEY);
  const current = createTouchSnapshot();

  if (hasAttributionParams(current.params)) {
    writeJson(LAST_TOUCH_STORAGE_KEY, current);
    return current;
  }

  if (existing) return existing;

  writeJson(LAST_TOUCH_STORAGE_KEY, current);
  return current;
}

function createSessionId() {
  if (hasWindow() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `landing_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function getSessionId() {
  const now = Date.now();
  const existing = readJson<SessionSnapshot>(SESSION_STORAGE_KEY);

  if (existing && now - existing.last_seen_at < SESSION_TIMEOUT_MS) {
    const updated = { ...existing, last_seen_at: now };
    writeJson(SESSION_STORAGE_KEY, updated);
    return existing.id;
  }

  const next = { id: createSessionId(), last_seen_at: now };
  writeJson(SESSION_STORAGE_KEY, next);
  return next.id;
}

function getBreakpoint(width: number) {
  if (width < 640) return "base";
  if (width < 768) return "sm";
  if (width < 1024) return "md";
  if (width < 1280) return "lg";
  return "xl";
}

function getDeviceType(width: number) {
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

function nullableParam(params: Record<string, string>, key: string) {
  return params[key] ?? null;
}

export function normalizeLandingPlan(plan: string | null | undefined) {
  if (!plan) return null;
  const normalized = plan.trim().toLowerCase();
  if (normalized === "starter" || normalized === "pro") return normalized;
  return null;
}

export function getSelectedLandingPlanFromUrl() {
  if (!hasWindow()) return null;
  return normalizeLandingPlan(new URLSearchParams(window.location.search).get("plan"));
}

export function getCommonLandingAnalyticsProperties(): LandingAnalyticsProperties {
  if (!hasWindow()) return {};

  const searchParams = new URLSearchParams(window.location.search);
  const currentParams = pickQueryParams(searchParams);
  const firstTouch = getFirstTouch();
  const lastTouch = getLastTouch();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  return {
    page_path: window.location.pathname,
    page_url: window.location.href,
    page_title: document.title,
    referrer: document.referrer || null,
    initial_referrer: firstTouch.initial_referrer,
    landing_page: firstTouch.landing_page,
    utm_source: nullableParam(currentParams, "utm_source"),
    utm_medium: nullableParam(currentParams, "utm_medium"),
    utm_campaign: nullableParam(currentParams, "utm_campaign"),
    utm_content: nullableParam(currentParams, "utm_content"),
    utm_term: nullableParam(currentParams, "utm_term"),
    first_utm_source: nullableParam(firstTouch.params, "utm_source"),
    first_utm_medium: nullableParam(firstTouch.params, "utm_medium"),
    first_utm_campaign: nullableParam(firstTouch.params, "utm_campaign"),
    first_utm_content: nullableParam(firstTouch.params, "utm_content"),
    first_utm_term: nullableParam(firstTouch.params, "utm_term"),
    first_gclid: nullableParam(firstTouch.params, "gclid"),
    first_fbclid: nullableParam(firstTouch.params, "fbclid"),
    first_li_fat_id: nullableParam(firstTouch.params, "li_fat_id"),
    last_utm_source: nullableParam(lastTouch.params, "utm_source"),
    last_utm_medium: nullableParam(lastTouch.params, "utm_medium"),
    last_utm_campaign: nullableParam(lastTouch.params, "utm_campaign"),
    last_utm_content: nullableParam(lastTouch.params, "utm_content"),
    last_utm_term: nullableParam(lastTouch.params, "utm_term"),
    last_gclid: nullableParam(lastTouch.params, "gclid"),
    last_fbclid: nullableParam(lastTouch.params, "fbclid"),
    last_li_fat_id: nullableParam(lastTouch.params, "li_fat_id"),
    gclid: nullableParam(currentParams, "gclid"),
    fbclid: nullableParam(currentParams, "fbclid"),
    li_fat_id: nullableParam(currentParams, "li_fat_id"),
    session_id: getSessionId(),
    viewport_width: viewportWidth,
    viewport_height: viewportHeight,
    breakpoint: getBreakpoint(viewportWidth),
    device_type: getDeviceType(viewportWidth),
  };
}

function sanitizeProperties(properties: LandingAnalyticsProperties) {
  const sanitized: LandingAnalyticsProperties = {};
  const blockedKeys = new Set(["email", "name", "notes", "website", "product_url", "productUrl"]);

  for (const [key, value] of Object.entries(properties)) {
    if (blockedKeys.has(key) || value === undefined) continue;
    sanitized[key] = value;
  }

  return sanitized;
}

export function captureLandingEvent(
  event: LandingAnalyticsEventName | string,
  properties: LandingAnalyticsProperties = {}
) {
  if (!hasWindow()) return;

  const eventProperties = sanitizeProperties({
    ...getCommonLandingAnalyticsProperties(),
    ...properties,
  });

  try {
    posthog.capture(event, eventProperties);
  } catch {
    // Ignore analytics failures.
  }

  try {
    trackMixpanelEvent(event, eventProperties);
  } catch {
    // Ignore analytics failures.
  }
}

export function getPassthroughQueryParams() {
  if (!hasWindow()) return new URLSearchParams();

  const params = new URLSearchParams();
  const current = new URLSearchParams(window.location.search);

  for (const key of LANDING_ATTRIBUTION_QUERY_KEYS) {
    const value = current.get(key);
    if (value) params.set(key, value);
  }

  return params;
}

export function appendCurrentLandingAttributionToHref(href: string) {
  if (!hasWindow() || !href) return href;

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return href;

    const current = getPassthroughQueryParams();

    for (const key of LANDING_ATTRIBUTION_QUERY_KEYS) {
      const value = current.get(key);
      if (!value || url.searchParams.has(key)) continue;
      url.searchParams.set(key, value);
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

export function getAttributionKeyGroups() {
  return { utmKeys: UTM_KEYS, clickIdKeys: CLICK_ID_KEYS };
}
